
import { Component, Vue, Watch } from 'vue-property-decorator'
import { AdminApi } from '@/wwapi/apis/admin-api'
import { cloneDeep } from 'lodash'
import { Form } from 'element-ui'

interface IReportType {
  id?: number
  name: string
  displayName: string
  description: string | null
  isActive: boolean
  periodType: string
  scheduleCron: string | null
  lastRunAt: string | null
}

interface IRecipientGroup {
  id?: number
  name: string
  description: string | null
  isActive: boolean
  recipients?: IRecipient[]
}

interface IRecipient {
  id?: number
  groupId: number
  email: string
  name: string | null
  isActive: boolean
}

interface ITypeGroup {
  id: number
  reportTypeId: number
  groupId: number
  isActive: boolean
  group?: IRecipientGroup
}

interface IReportLog {
  id: number
  reportTypeId: number
  status: string
  recipientsJson: Array<{ email: string; name: string | null }> | null
  resultJson: Record<string, unknown> | null
  errorMessage: string | null
  startedAt: string
  completedAt: string | null
  durationMs: number | null
}

const defaultType: IReportType = {
  name: '',
  displayName: '',
  description: null,
  isActive: true,
  periodType: 'daily',
  scheduleCron: null,
  lastRunAt: null
}

const defaultGroup: IRecipientGroup = {
  name: '',
  description: null,
  isActive: true
}

const defaultRecipient: IRecipient = {
  groupId: 0,
  email: '',
  name: null,
  isActive: true
}

@Component({ name: 'ReportingPage' })
export default class extends Vue {
  private activeTab = 'types'

  // Report Types
  private reportTypes: IReportType[] = []
  private typesLoading = false
  private typeDialogVisible = false
  private typeDialogStatus = ''
  private tempType: IReportType = cloneDeep(defaultType)
  private deleteTypeVisible = false
  private deleteTypeId: number | null = null
  private triggerLoading: number | null = null
  private typeRules = {
    name: [{ required: true, message: 'Name is required', trigger: 'blur' }],
    displayName: [{ required: true, message: 'Display name is required', trigger: 'blur' }]
  }

  // Recipient Groups
  private recipientGroups: IRecipientGroup[] = []
  private groupsLoading = false
  private groupDialogVisible = false
  private groupDialogStatus = ''
  private tempGroup: IRecipientGroup = cloneDeep(defaultGroup)
  private deleteGroupVisible = false
  private deleteGroupId: number | null = null
  private groupRules = {
    name: [{ required: true, message: 'Group name is required', trigger: 'blur' }]
  }

  // Recipients
  private recipientDialogVisible = false
  private recipientDialogStatus = ''
  private tempRecipient: IRecipient = cloneDeep(defaultRecipient)
  private selectedRecipientGroupId: number | null = null
  private recipientRules = {
    email: [
      { required: true, message: 'Email is required', trigger: 'blur' },
      { type: 'email', message: 'Please enter a valid email', trigger: 'blur' }
    ]
  }

  // Assignments
  private selectedTypeId: number | null = null
  private assignments: ITypeGroup[] = []
  private assignmentsLoading = false
  private assignGroupId: number | null = null
  private testSendLoading = false

  // Logs
  private reportLogs: IReportLog[] = []
  private logsLoading = false
  private logsPage = 1
  private logsPageSize = 20
  private logsTotal = 0
  private logDetailVisible = false
  private selectedLog: IReportLog | null = null

  get availableGroups(): IRecipientGroup[] {
    const assignedIds = new Set(this.assignments.map(a => a.groupId))
    return this.recipientGroups.filter(g => !assignedIds.has(g.id!))
  }

  created() {
    this.fetchReportTypes()
    this.fetchRecipientGroups()
  }

  @Watch('activeTab')
  onTabChange(tab: string) {
    if (tab === 'logs') this.fetchLogs()
    if (tab === 'assignments') {
      this.fetchReportTypes()
      this.fetchRecipientGroups()
    }
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString()
  }

  // ===================== REPORT TYPES =====================

  private async fetchReportTypes() {
    this.typesLoading = true
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.getReportTypes()
      this.reportTypes = data.data || []
      if (this.reportTypes.length > 0 && !this.selectedTypeId) {
        this.selectedTypeId = this.reportTypes[0].id!
        this.fetchAssignments()
      }
    } catch {
      this.$notify({ title: 'Error', message: 'Failed to load report types', type: 'error', duration: 2000 })
    } finally {
      this.typesLoading = false
    }
  }

  private handleCreateType() {
    this.tempType = cloneDeep(defaultType)
    this.typeDialogStatus = 'create'
    this.typeDialogVisible = true
    this.$nextTick(() => { (this.$refs.typeForm as Form)?.clearValidate() })
  }

  private handleUpdateType(row: IReportType) {
    this.tempType = Object.assign({}, row)
    this.typeDialogStatus = 'update'
    this.typeDialogVisible = true
    this.$nextTick(() => { (this.$refs.typeForm as Form)?.clearValidate() })
  }

  private createType() {
    (this.$refs.typeForm as Form).validate(async(valid) => {
      if (!valid) return
      try {
        const adminApi = new AdminApi()
        await adminApi.createReportType({
          name: this.tempType.name,
          displayName: this.tempType.displayName,
          description: this.tempType.description,
          isActive: this.tempType.isActive,
          periodType: this.tempType.periodType,
          scheduleCron: this.tempType.scheduleCron
        })
        this.typeDialogVisible = false
        this.$notify({ title: 'Success', message: 'Report type created', type: 'success', duration: 2000 })
        await this.fetchReportTypes()
      } catch (error: any) {
        this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
      }
    })
  }

  private updateType() {
    (this.$refs.typeForm as Form).validate(async(valid) => {
      if (!valid) return
      try {
        const adminApi = new AdminApi()
        await adminApi.updateReportType(this.tempType.id!, {
          displayName: this.tempType.displayName,
          description: this.tempType.description,
          isActive: this.tempType.isActive,
          periodType: this.tempType.periodType,
          scheduleCron: this.tempType.scheduleCron
        })
        this.typeDialogVisible = false
        this.$notify({ title: 'Success', message: 'Report type updated', type: 'success', duration: 2000 })
        await this.fetchReportTypes()
      } catch (error: any) {
        this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
      }
    })
  }

  private showDeleteType(row: IReportType) {
    this.deleteTypeId = row.id!
    this.deleteTypeVisible = true
  }

  private async deleteType() {
    try {
      const adminApi = new AdminApi()
      await adminApi.deleteReportType(this.deleteTypeId!)
      this.$notify({ title: 'Success', message: 'Report type deleted', type: 'success', duration: 2000 })
      await this.fetchReportTypes()
    } catch (error: any) {
      this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
    } finally {
      this.deleteTypeVisible = false
      this.deleteTypeId = null
    }
  }

  private async handleTriggerReport(row: IReportType) {
    this.triggerLoading = row.id!
    try {
      const adminApi = new AdminApi()
      await adminApi.triggerDailyReport(row.name)
      this.$notify({ title: 'Success', message: 'Report executed successfully', type: 'success', duration: 3000 })
      await this.fetchReportTypes()
    } catch (error: any) {
      this.$notify({ title: 'Error', message: error.response?.data?.error || 'Failed to execute report', type: 'error', duration: 3000 })
    } finally {
      this.triggerLoading = null
    }
  }

  // ===================== RECIPIENT GROUPS =====================

  private async fetchRecipientGroups() {
    this.groupsLoading = true
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.getRecipientGroups()
      this.recipientGroups = data.data || []
    } catch {
      this.$notify({ title: 'Error', message: 'Failed to load groups', type: 'error', duration: 2000 })
    } finally {
      this.groupsLoading = false
    }
  }

  private handleCreateGroup() {
    this.tempGroup = cloneDeep(defaultGroup)
    this.groupDialogStatus = 'create'
    this.groupDialogVisible = true
    this.$nextTick(() => { (this.$refs.groupForm as Form)?.clearValidate() })
  }

  private handleUpdateGroup(row: IRecipientGroup) {
    this.tempGroup = Object.assign({}, row)
    this.groupDialogStatus = 'update'
    this.groupDialogVisible = true
    this.$nextTick(() => { (this.$refs.groupForm as Form)?.clearValidate() })
  }

  private createGroup() {
    (this.$refs.groupForm as Form).validate(async(valid) => {
      if (!valid) return
      try {
        const adminApi = new AdminApi()
        await adminApi.createRecipientGroup({
          name: this.tempGroup.name,
          description: this.tempGroup.description,
          isActive: this.tempGroup.isActive
        })
        this.groupDialogVisible = false
        this.$notify({ title: 'Success', message: 'Group created', type: 'success', duration: 2000 })
        await this.fetchRecipientGroups()
      } catch (error: any) {
        this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
      }
    })
  }

  private updateGroup() {
    (this.$refs.groupForm as Form).validate(async(valid) => {
      if (!valid) return
      try {
        const adminApi = new AdminApi()
        await adminApi.updateRecipientGroup(this.tempGroup.id!, {
          name: this.tempGroup.name,
          description: this.tempGroup.description,
          isActive: this.tempGroup.isActive
        })
        this.groupDialogVisible = false
        this.$notify({ title: 'Success', message: 'Group updated', type: 'success', duration: 2000 })
        await this.fetchRecipientGroups()
      } catch (error: any) {
        this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
      }
    })
  }

  private showDeleteGroup(row: IRecipientGroup) {
    this.deleteGroupId = row.id!
    this.deleteGroupVisible = true
  }

  private async deleteGroup() {
    try {
      const adminApi = new AdminApi()
      await adminApi.deleteRecipientGroup(this.deleteGroupId!)
      this.$notify({ title: 'Success', message: 'Group deleted', type: 'success', duration: 2000 })
      await this.fetchRecipientGroups()
    } catch (error: any) {
      this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
    } finally {
      this.deleteGroupVisible = false
      this.deleteGroupId = null
    }
  }

  // ===================== RECIPIENTS =====================

  private handleAddRecipient(group: IRecipientGroup) {
    this.selectedRecipientGroupId = group.id!
    this.tempRecipient = cloneDeep(defaultRecipient)
    this.tempRecipient.groupId = group.id!
    this.recipientDialogStatus = 'create'
    this.recipientDialogVisible = true
    this.$nextTick(() => { (this.$refs.recipientForm as Form)?.clearValidate() })
  }

  private handleEditRecipient(row: IRecipient) {
    this.tempRecipient = Object.assign({}, row)
    this.recipientDialogStatus = 'update'
    this.recipientDialogVisible = true
    this.$nextTick(() => { (this.$refs.recipientForm as Form)?.clearValidate() })
  }

  private createRecipient() {
    (this.$refs.recipientForm as Form).validate(async(valid) => {
      if (!valid) return
      try {
        const adminApi = new AdminApi()
        await adminApi.addReportRecipient(this.selectedRecipientGroupId!, {
          email: this.tempRecipient.email,
          name: this.tempRecipient.name,
          isActive: this.tempRecipient.isActive
        })
        this.recipientDialogVisible = false
        this.$notify({ title: 'Success', message: 'Recipient added', type: 'success', duration: 2000 })
        await this.fetchRecipientGroups()
      } catch (error: any) {
        this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
      }
    })
  }

  private updateRecipient() {
    (this.$refs.recipientForm as Form).validate(async(valid) => {
      if (!valid) return
      try {
        const adminApi = new AdminApi()
        await adminApi.updateReportRecipient(this.tempRecipient.id!, {
          email: this.tempRecipient.email,
          name: this.tempRecipient.name,
          isActive: this.tempRecipient.isActive
        })
        this.recipientDialogVisible = false
        this.$notify({ title: 'Success', message: 'Recipient updated', type: 'success', duration: 2000 })
        await this.fetchRecipientGroups()
      } catch (error: any) {
        this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
      }
    })
  }

  private async removeRecipient(row: IRecipient) {
    try {
      const adminApi = new AdminApi()
      await adminApi.deleteReportRecipient(row.id!)
      this.$notify({ title: 'Success', message: 'Recipient removed', type: 'success', duration: 2000 })
      await this.fetchRecipientGroups()
    } catch (error: any) {
      this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
    }
  }

  // ===================== ASSIGNMENTS =====================

  private async fetchAssignments() {
    if (!this.selectedTypeId) return
    this.assignmentsLoading = true
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.getGroupsForReportType(this.selectedTypeId)
      this.assignments = data.data || []
    } catch {
      this.$notify({ title: 'Error', message: 'Failed to load assignments', type: 'error', duration: 2000 })
    } finally {
      this.assignmentsLoading = false
    }
  }

  private async handleAssignGroup(groupId: number) {
    if (!this.selectedTypeId) return
    try {
      const adminApi = new AdminApi()
      await adminApi.assignGroupToReportType(this.selectedTypeId, groupId)
      this.$notify({ title: 'Success', message: 'Group assigned', type: 'success', duration: 2000 })
      await this.fetchAssignments()
    } catch (error: any) {
      this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
    } finally {
      this.assignGroupId = null
    }
  }

  private async removeAssignment(row: ITypeGroup) {
    try {
      const adminApi = new AdminApi()
      await adminApi.removeTypeGroupAssignment(row.id)
      this.$notify({ title: 'Success', message: 'Assignment removed', type: 'success', duration: 2000 })
      await this.fetchAssignments()
    } catch (error: any) {
      this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Failed', type: 'error', duration: 2000 })
    }
  }

  private async handleTestSend() {
    if (!this.selectedTypeId) return
    this.testSendLoading = true
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.testSendReport(this.selectedTypeId)
      const result = data.data
      this.$notify({
        title: 'Success',
        message: `Test sent to ${result.sentCount}/${result.totalRecipients} recipients`,
        type: 'success',
        duration: 3000
      })
    } catch (error: any) {
      this.$notify({ title: 'Error', message: error.response?.data?.status?.message || 'Test send failed', type: 'error', duration: 3000 })
    } finally {
      this.testSendLoading = false
    }
  }

  // ===================== LOGS =====================

  private async fetchLogs() {
    this.logsLoading = true
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.getReportLogs({ page: this.logsPage, pageSize: this.logsPageSize })
      this.reportLogs = data.data.items || []
      this.logsTotal = data.data.total || 0
    } catch {
      this.$notify({ title: 'Error', message: 'Failed to load logs', type: 'error', duration: 2000 })
    } finally {
      this.logsLoading = false
    }
  }

  private handleLogsPageChange(page: number) {
    this.logsPage = page
    this.fetchLogs()
  }

  private showLogDetail(row: IReportLog) {
    this.selectedLog = row
    this.logDetailVisible = true
  }
}
